import styles from '@/components/orders/ConfirmOrderModal.module.css'
import { Order, OrderConfirmation, PaymentMethod, Printer, SharedProps } from '@/types'
import { formatCurrency, getCustomerInfo } from '@/utils/format'
import { usePage } from '@inertiajs/react'
import {
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import {
  IconCalendarEvent,
  IconCash,
  IconClockHour4,
  IconCreditCard,
  IconPrinter,
  IconShirt,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { ChangeEvent, ReactNode, useState } from 'react'

function PaymentChoiceTile({
  icon,
  color,
  label,
  description,
  loading,
  disabled,
  onClick,
}: {
  icon: ReactNode
  color: string
  label: string
  description: string
  loading: boolean
  disabled: boolean
  onClick: () => void
}) {
  return (
    <UnstyledButton
      className={styles.tile}
      style={{ '--tile-color': `var(--mantine-color-${color}-5)` }}
      data-loading={loading || undefined}
      disabled={disabled}
      onClick={onClick}
    >
      <Paper className={styles.card} withBorder radius="lg" p="lg" h="100%">
        <Stack align="center" gap={6}>
          <ThemeIcon size={56} radius="xl" variant="light" color={color}>
            {loading ? <Loader size={22} color={color} /> : icon}
          </ThemeIcon>
          <Text fw={700}>{label}</Text>
          <Text size="xs" c="dimmed" ta="center">
            {description}
          </Text>
        </Stack>
      </Paper>
    </UnstyledButton>
  )
}

export default function ConfirmOrderModal({
  order,
  printers,
  opened,
  confirming,
  onClose,
  onConfirm,
}: {
  order: Order
  printers: Printer[]
  opened: boolean
  confirming: boolean
  onClose: () => void
  onConfirm: (confirmation: OrderConfirmation) => void
}) {
  const { current_organization } = usePage<SharedProps>().props
  const [shouldPrint, setShouldPrint] = useState(true)
  const [chosenPayment, setChosenPayment] = useState<PaymentMethod | 'later' | null>(null)
  const [storedPrinterId, setStoredPrinterId] = useLocalStorage<string | null>({
    key: 'comptoir-printer-id',
    defaultValue: null,
  })

  const selectedPrinter = printers.find((printer) => String(printer.id) === storedPrinterId) ?? printers[0]
  const showPrinterPicker = Boolean(current_organization?.cloudprnt_enabled) && shouldPrint && printers.length > 1
  const printerOptions = printers.map((printer) => ({ value: String(printer.id), label: printer.name }))
  const articlesCount = order.articles.length

  const choose = (payment: PaymentMethod | 'later') => {
    if (confirming) return

    setChosenPayment(payment)
    onConfirm({
      paymentMethod: payment === 'later' ? null : payment,
      shouldPrint,
      printerId: selectedPrinter?.id ?? null,
    })
  }
  const isLoading = (payment: PaymentMethod | 'later') => confirming && chosenPayment === payment
  const isDisabled = (payment: PaymentMethod | 'later') => confirming && chosenPayment !== payment
  const payByCard = () => choose('card')
  const payByCash = () => choose('cash')
  const payAtPickup = () => choose('later')
  const handlePrintChange = (event: ChangeEvent<HTMLInputElement>) => setShouldPrint(event.currentTarget.checked)
  const handlePrinterChange = (value: string | null) => setStoredPrinterId(value)

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Valider la commande
        </Text>
      }
      centered
      size="lg"
      radius="lg"
      padding="lg"
      overlayProps={{ backgroundOpacity: 0.5, blur: 3 }}
    >
      <Stack gap="lg">
        <Paper radius="md" p="md" className={styles.summary}>
          <Group justify="space-between" align="center" wrap="nowrap">
            <Stack gap={6}>
              <Text fw={700} size="lg" lh={1.2}>
                {getCustomerInfo(order).name}
              </Text>
              <Group gap="md">
                <Group gap={6}>
                  <IconShirt size={16} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed">
                    {articlesCount} article{articlesCount > 1 ? 's' : ''}
                  </Text>
                </Group>
                {order.planned_date && (
                  <Group gap={6}>
                    <IconCalendarEvent size={16} color="var(--mantine-color-dimmed)" />
                    <Text size="sm" c="dimmed">
                      Prête le {dayjs(order.planned_date).format('dddd D MMMM')}
                    </Text>
                  </Group>
                )}
              </Group>
            </Stack>
            <Stack gap={0} align="flex-end">
              <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                Total
              </Text>
              <Text fz={34} fw={800} lh={1.1}>
                {formatCurrency(order.price)}
              </Text>
            </Stack>
          </Group>
        </Paper>

        <Stack gap="sm">
          <Group justify="space-between" align="baseline">
            <Text fw={700}>Comment le client paie-t-il ?</Text>
            <Text size="xs" c="dimmed">
              Un clic valide la commande
            </Text>
          </Group>
          <SimpleGrid cols={3} spacing="sm">
            <PaymentChoiceTile
              icon={<IconCreditCard size={28} />}
              color="blue"
              label="Carte bancaire"
              description="Encaissé maintenant"
              loading={isLoading('card')}
              disabled={isDisabled('card')}
              onClick={payByCard}
            />
            <PaymentChoiceTile
              icon={<IconCash size={28} />}
              color="teal"
              label="Espèces"
              description="Encaissé maintenant"
              loading={isLoading('cash')}
              disabled={isDisabled('cash')}
              onClick={payByCash}
            />
            <PaymentChoiceTile
              icon={<IconClockHour4 size={28} />}
              color="orange"
              label="Au retrait"
              description="Paiera en récupérant sa commande"
              loading={isLoading('later')}
              disabled={isDisabled('later')}
              onClick={payAtPickup}
            />
          </SimpleGrid>
        </Stack>

        <Divider />

        <Group justify="space-between" wrap="nowrap">
          <Switch label="Imprimer les tickets" checked={shouldPrint} onChange={handlePrintChange} size="md" />
          {showPrinterPicker && (
            <Select
              aria-label="Imprimante"
              leftSection={<IconPrinter size={16} />}
              data={printerOptions}
              value={String(selectedPrinter.id)}
              onChange={handlePrinterChange}
              allowDeselect={false}
              size="sm"
              w={180}
            />
          )}
        </Group>
      </Stack>
    </Modal>
  )
}
